import {
  Brush,
  Building2,
  ChevronsUpDown,
  Languages,
  LogOut,
  Shield,
  Wrench,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { getSettingsPagePath } from '@/features/settings/settingsPaths'
import { useGetUserSession } from '@/shared/hooks/api/auth'
import { useCurrentUser } from '@/shared/hooks/api/user'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import useDialogState from '@/shared/hooks/use-dialog-state'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { SignOutDialog } from '@/components/sign-out-dialog'

type ProfileDropdownProps = {
  /** 'default' = avatar button for headers; 'sidebar' = full user block in sidebar footer */
  variant?: 'default' | 'sidebar'
}

function getInitials(firstName?: string, lastName?: string): string {
  const parts = [firstName?.[0], lastName?.[0]].filter(Boolean).join('')
  return parts.toUpperCase() || 'U'
}

function UserMenuContent({ onSignOut }: { onSignOut: () => void }) {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()
  const { data: session } = useGetUserSession()
  const canWriteOrgData = useCanWriteOrgData()
  const showAdminConsole = session?.isArtemAdmin === true

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <Link to={getSettingsPagePath(pathname, 'account')}>
            <Wrench />
            {t('settings.account')}
          </Link>
        </DropdownMenuItem>
        {canWriteOrgData && (
          <DropdownMenuItem asChild>
            <Link to={getSettingsPagePath(pathname, 'organization')}>
              <Building2 />
              {t('settings.organization')}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to={getSettingsPagePath(pathname, 'theme')}>
            <Brush />
            {t('settings.theme')}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={getSettingsPagePath(pathname, 'language')}>
            <Languages />
            {t('settings.language')}
          </Link>
        </DropdownMenuItem>
        {showAdminConsole && (
          <DropdownMenuItem asChild>
            <a href='/admin' target='_blank' rel='noopener noreferrer'>
              <Shield />
              {t('profile.adminConsole')}
            </a>
          </DropdownMenuItem>
        )}
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem variant='destructive' onClick={onSignOut}>
        <LogOut />
        {t('profile.signOut')}
      </DropdownMenuItem>
    </>
  )
}

function ProfileDropdownSidebar() {
  const { isMobile } = useSidebar()
  const [open, setOpen] = useDialogState()
  const { data: user } = useCurrentUser()

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  const displayName = fullName || user?.email || 'User'
  const email = user?.email || ''
  const initials = getInitials(user?.firstName, user?.lastName)

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size='lg'
                className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              >
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarFallback className='rounded-lg'>
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className='grid flex-1 text-start text-sm leading-tight'>
                  <span className='truncate font-semibold'>{displayName}</span>
                  <span className='truncate text-xs'>{email}</span>
                </div>
                <ChevronsUpDown className='ms-auto size-4' />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className='w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg'
              side={isMobile ? 'bottom' : 'right'}
              align='end'
              sideOffset={4}
            >
              <DropdownMenuLabel className='p-0 font-normal'>
                <div className='flex items-center gap-2 px-1 py-1.5 text-start text-sm'>
                  <Avatar className='h-8 w-8 rounded-lg'>
                    <AvatarFallback className='rounded-lg'>
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className='grid flex-1 text-start text-sm leading-tight'>
                    <span className='truncate font-semibold'>
                      {displayName}
                    </span>
                    <span className='truncate text-xs'>{email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <UserMenuContent onSignOut={() => setOpen(true)} />
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <SignOutDialog open={!!open} onOpenChange={setOpen} />
    </>
  )
}

function ProfileDropdownDefault() {
  const [open, setOpen] = useDialogState()
  const { data: user } = useCurrentUser()

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  const displayName = fullName || user?.email || 'User'
  const email = user?.email || 'Signed in'
  const initials = getInitials(user?.firstName, user?.lastName)

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' className='relative h-8 w-8 rounded-full'>
            <Avatar className='h-8 w-8'>
              <AvatarImage src='/avatars/01.png' alt={displayName} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className='w-56' align='end'>
          <DropdownMenuLabel className='font-normal'>
            <div className='flex flex-col gap-1.5'>
              <p className='text-sm leading-none font-medium'>{displayName}</p>
              <p className='text-xs leading-none text-muted-foreground'>
                {email}
              </p>
            </div>
          </DropdownMenuLabel>
          <UserMenuContent onSignOut={() => setOpen(true)} />
        </DropdownMenuContent>
      </DropdownMenu>
      <SignOutDialog open={!!open} onOpenChange={setOpen} />
    </>
  )
}

export function ProfileDropdown({ variant = 'default' }: ProfileDropdownProps) {
  if (variant === 'sidebar') {
    return <ProfileDropdownSidebar />
  }
  return <ProfileDropdownDefault />
}
